import React, { useEffect, useState } from "react";
import { Button, Table, Tag, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { toAbsoluteUrl } from "../../../helpers/AssetsHelpers";
import {
  ExportHeader,
  SearchHeader,
} from "../../../components/layout/header/HeaderList";
import { Link } from "react-router-dom";

// Data imports
import { getListeClients } from "../../../data/client";
import {
  getAllListeEquipeMission,
  getListeExercice,
} from "../../../data/exercice";

// Component for date & user display
import { DateUserListe } from "../../../components/layout/pageData/DateUserListe";
import { width } from "@fortawesome/free-solid-svg-icons/faListSquares";

const ListeEqupeMission = () => {
  // -------------------------
  // States
  // -------------------------
  const [equipeClients, setEquipeClients] = useState([]); // Liste finale des missions avec clients
  const [equipeMission, setEquipeMission] = useState([]); // Liste des missions avec équipes
  const [equipes, setEquipes] = useState([]); // Liste des exercices
  const [filtersItems, setFiltersItems] = useState([]); // Liste filtrée pour le tableau
  const [message, setMessage] = useState(false); // Message d’erreur ou succès
  const [error, setError] = useState(false); // Gestion des erreurs
  const [loading, setLoading] = useState(false); // Loader
  const [open, setOpen] = useState(false); // Etat modal
  const [isModalOpen, setIsModalOpen] = useState(false); // Ouverture modal upload
  const [selectedRows, setSelectedRows] = useState([]);

  // Couleurs pour avatars dynamiques
  const colors = ["primary", "danger", "warning", "info", "success"];

  // Paramètres de pagination du tableau
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  // -------------------------
  // Fetch data au montage
  // -------------------------
  useEffect(() => {
    fetchClientExercice();
  }, []);

  // Quand on a les exercices → on récupère les missions
  useEffect(() => {
    if (equipes.length > 0) getAllEquipeMission();
  }, [equipes]);

  // Quand on a les missions → on récupère les clients liés
  useEffect(() => {
    if (equipeMission.length > 0) fetchClients();
  }, [equipeMission]);

  // -------------------------
  // Récupération des exercices
  // -------------------------
  const fetchClientExercice = () => {
    setLoading(true);
    getListeExercice().then((res) => {
      setEquipes(res);
      setLoading(false);
    });
  };

  // -------------------------
  // Récupération des missions liées aux exercices
  // -------------------------
  const getAllEquipeMission = () => {
    getAllListeEquipeMission().then((res) => {
      const elements = equipes.map((item) => {
        const equipesByExercice = res.filter(
          (opt) => opt.exercice_id === item.id
        );
        return { ...item, equipes: equipesByExercice };
      });
      setEquipeMission(elements);
    });
  };

  // -------------------------
  // Récupération des clients liés aux missions
  // -------------------------
  const fetchClients = () => {
    setLoading(true);
    getListeClients().then((res) => {
      const elements = equipeMission
        .filter((item) => item.equipes.length > 0)
        .map((item) => ({
          ...item,
          client: res.result.find((opt) => opt.id === item.client_id),
        }));

      setEquipeClients(elements);
      setFiltersItems(elements);
      setLoading(false);
    });
  };

  // -------------------------
  // Annulation modal
  // -------------------------
  const handleCancel = () => {
    setOpen(false);
    setError(false);
  };

  // -------------------------
  // Gestion de la recherche
  // -------------------------
  const handleSearchChange = (e) => {
    const searchTerm = e.target.value.toLowerCase();

    const filtered = equipeClients.filter((item) => {
      // recherche sur mission / année / client
      const missionMatch =
        item.mission?.toLowerCase().includes(searchTerm) ||
        item.annee?.toLowerCase().includes(searchTerm) ||
        item.client?.sigle?.toLowerCase().includes(searchTerm) ||
        item.client?.denomination?.toLowerCase().includes(searchTerm);

      // recherche sur les membres de l'équipe
      const equipeMatch = item.equipes?.some((membre) => {
        const nom = membre.nom_user?.toLowerCase() || "";
        const prenom = membre.prenom_user?.toLowerCase() || "";

        const prenomParts = prenom.split(" ").filter(Boolean);

        // Variante 1 : Nom + Prénom(s)
        const fullNameParts = [nom, ...prenomParts];
        const initialsWithNom = fullNameParts
          .map((p) => p[0])
          .join("")
          .toLowerCase();

        // Variante 2 : Seulement Prénom(s)
        const initialsWithoutNom = prenomParts
          .map((p) => p[0])
          .join("")
          .toLowerCase();

        return (
          nom.includes(searchTerm) ||
          prenom.includes(searchTerm) ||
          initialsWithNom.startsWith(searchTerm) ||
          initialsWithoutNom.startsWith(searchTerm)
        );
      });

      return missionMatch || equipeMatch;
    });
    setFiltersItems(filtered);
  };

  // -------------------------
  // Gestion de la pagination du tableau
  // -------------------------
  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({ pagination, filters, ...sorter });
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setEquipeClients([]);
    }
  };

  // -------------------------
  // Colonnes du tableau
  // -------------------------
  const columns = [
    {
      title: "Equipes",
      dataIndex: "equipe",
      width: "220px",
      render: (_, record) => (
        <div className="d-flex align-center">
          {record?.equipes?.map((item, index) => (
            <div
              key={index}
              className="symbol symbol-35px symbol-circle"
              data-bs-toggle="tooltip"
              title={item.nom_user + " " + item.prenom_user}
            >
              {item.avatar ? (
                <div className="symbol-label">
                  <img
                    src={toAbsoluteUrl(`/media/${item.avatar}`)}
                    alt={item.nom_user}
                    className="w-100"
                  />
                </div>
              ) : (
                <Tooltip title={item.nom_user + " " + item.prenom_user}>
                  <span
                    className={`symbol-label bg-${
                      colors[Math.floor(Math.random() * colors.length)]
                    } text-inverse-primary fw-bolder`}
                  >
                    {item.nom_user[0].toUpperCase()}
                  </span>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      ),
    },

    {
      title: "Mission",
      dataIndex: "mission",
      align: "center",
      render: (_, record) => (
        <div className="d-flex align-items-center justify-content-center">
          <div className="d-flex flex-column text-center">
            <div
              className="badge badge-light fw-bolder"
              style={{ fontSize: "15px" }}
            >
              {`${record?.mission} - ${record?.annee}`}
            </div>
            <span className="table-strong">{record.client?.sigle}</span>
            <div className="text-gray-600 mb-1">
              <DateUserListe
                date={record.createdAt}
                user={
                  record?.prenom_user
                    ? `${record.nom_user} ${record.prenom_user}`
                    : record.nom_user
                }
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Dénomination",
      dataIndex: "denomination",
      width: "300px",
      render: (_, record) => (
        <div className="">
          <div className="d-flex flex-column ">
            <span className="table-strong">{record.client?.denomination}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "statut",
      align: "center",
      width: "150px",
      render: (_, record) =>
        !record?.isCloture ? (
          record?.lettre_mission === 0 ? (
            <Tag color="yellow">En attente</Tag>
          ) : (
            <Tag color="blue">En cours</Tag>
          )
        ) : (
          <Tag color="green">Clôturé</Tag>
        ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, record) => (
        <Link
          style={{ marginRight: "10px" }}
          to={`/clients/mission/exploitation/${record.id}`}
          state={{ record: record.client, mission: record }}
        >
          <Tooltip title="Consulter Exercice">
            <button
              type="button"
              className="btn btn-primary"
              data-bs-toggle="modal"
              data-bs-target="#kt_modal_add_user"
            >
              <span className="d-flex align-items-center">
                <EyeOutlined style={{ fontSize: "2px" }} />
                Consulter
              </span>
            </button>
          </Tooltip>
        </Link>
      ),
    },
  ];

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="card w-100">
      {/* Message d'erreur */}
      {error && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{message}</div>
        </div>
      )}

      {/* Header */}
      <div className="card-header border-0  my-5">
        <SearchHeader handleSearch={handleSearchChange} />
        <div className="card-toolbar">
          <div className="d-flex justify-content-end">
            {/* <ExportHeader contentComponent={equipeClients} /> */}
            {selectedRows.length > 0 && (
              <ExportHeader contentComponent={selectedRows} />
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-body py-4">
        <div className="table-responsive">
          <Table
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={filtersItems}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowSelection={{
              type: "checkbox",
              onChange: (selectedRowKeys, selectedRows) => {
                //console.log(selectedRowKeys, selectedRows);
                // Ajoutez ici la logique à exécuter lors de la sélection des lignes
                setSelectedRows(selectedRows); // Met à jour l'état avec les lignes sélectionnées
              },
            }}
          />
        </div>
      </div>

      {/* Modal Upload */}
      {isModalOpen && (
        <div
          className="position-fixed top-0 bottom-0 z-index-1000 h-100"
          style={{
            insetInlineEnd: "0",
            insetInlineStart: "0",
            backgroundColor: "rgba(0, 0, 0, 0.45)",
          }}
        >
          {/* Modal Upload (à réactiver si besoin) */}
          {/* <FileUploadModal onClose={handleCloseModal} client={client} fetchClients={fetchClients} /> */}
        </div>
      )}
    </div>
  );
};

export default ListeEqupeMission;
